import { FlowComponent } from '../../../flow.min.js';
import styles from './styles.js';
import { ValidatorMixin } from './validator.mixin.js';

const template = FlowComponent.html`
    <div class="mb-4 relative">
        <label class="block text-sm font-medium text-gray-700 mb-1">${'label'}</label>
        <div class="relative flex items-center w-full">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 z-10">
                <slot name="prefix"></slot>
            </div>
            <input 
                type="${'type'}" 
                name="${'name'}" 
                placeholder="${'placeholder'}" 
                value="${'value'}"
                ?disabled="${'disabled'}"
                class="appearance-none rounded-lg relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm invalid:border-red-500 invalid:text-red-900 focus:invalid:border-red-500 focus:invalid:ring-red-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed [&:has(~.prefix-slot[name=prefix]):not(:placeholder-shown)]:pl-10 [&:has(~.suffix-slot[name=action])]:pr-10"
            >
            <div class="password-toggle absolute inset-y-0 left-0 pl-3 flex items-center cursor-pointer text-gray-400 hover:text-gray-600 z-20" style="display: none;">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
            </div>
            <div class="absolute inset-y-0 right-0 pr-2 flex items-center cursor-pointer z-10">
                <slot name="action" class="suffix-slot"></slot>
            </div>
        </div>
        <!-- Slot opcional para mensajes de error custom si no usamos los nativos -->
        <p class="mt-1 text-sm text-red-600 hidden peer-invalid:block"></p>
    </div>
`;

// Aplicamos el Mixin aquí: ValidatorMixin(FlowComponent)
export class FlowInput extends ValidatorMixin(FlowComponent) {
    static formAssociated = true;
    #internals;

    static get observedAttributes() {
        return ['type', 'name', 'placeholder', 'label', 'value', 'disabled', 'list'];
    }

    constructor() {
        super();
        this.#internals = this.attachInternals();
    }

    static get styles() { return styles; }
    static get template() { return template; }

    get disabled() {
        return this.hasAttribute('disabled');
    }

    set disabled(val) {
        if (val) {
            this.setAttribute('disabled', '');
        } else {
            this.removeAttribute('disabled');
        }
    }

    onMount() {
        super.onMount(); // Esto ejecuta el onMount del Mixin también
        const input = this.root.querySelector('input');
        const type = this.getAttribute('type') || 'text';
        
        // Configurar toggle de password si aplica
        if (type === 'password') {
            this.#setupPasswordToggle(input);
        }
        
        // Asignar dinámicamente el list si existe (para datalist) sin romper los demás inputs
        if (this.hasAttribute('list')) {
            const listId = this.getAttribute('list');
            input.setAttribute('list', listId);
            
            // Los datalists deben existir en el mismo Shadow DOM que el input para funcionar
            // Buscarlo en el Light DOM (hijos directos) o en el DOM padre (donde vive este componente)
            setTimeout(() => {
                const dataList = this.querySelector(`datalist#${listId}`) || this.getRootNode().querySelector(`datalist#${listId}`);
                if (dataList && !this.root.querySelector(`datalist#${listId}`)) {
                    this.root.appendChild(dataList.cloneNode(true));
                }
            }, 0);
        }
        
        // Función para sincronizar el estado interno con el form padre
        const syncValidity = () => {
            this.setAttribute('value', input.value);
            this.#internals.setFormValue(input.value);
            
            // Magia: Copiamos el estado de validez del input interno al componente custom
            // Así el formulario padre sabe si este <flow-input> es válido.
            if (!input.checkValidity()) {
                this.#internals.setValidity(
                    input.validity, 
                    input.validationMessage, 
                    input
                );
            } else {
                this.#internals.setValidity({});
            }
        };

        input.addEventListener('input', syncValidity);
        
        // Sincronización inicial
        this.#internals.setFormValue(this.getAttribute('value') || '');
    }

    formResetCallback() {
        const initialValue = this.getAttribute('value') || '';
        this.value = initialValue;
        this.#internals.setFormValue(initialValue);
    }

    #setupPasswordToggle(input) {
        const toggleBtn = this.root.querySelector('.password-toggle');
        if (!toggleBtn) return;
        
        // Mostrar el botón de toggle
        toggleBtn.style.display = 'flex';
        
        // Agregar padding al input para el icono
        input.style.paddingLeft = '2.5rem';
        
        let cursorPosition = 0;
        let isMouseDown = false;
        
        const showPassword = () => {
            cursorPosition = input.selectionStart;
            input.type = 'text';
        };
        
        const hidePassword = () => {
            input.type = 'password';
            // Solo restaurar el focus si el input tenía focus antes
            if (document.activeElement === input) {
                input.setSelectionRange(cursorPosition, cursorPosition);
            }
        };
        
        // Eventos para mouse
        toggleBtn.addEventListener('mousedown', (e) => {
            e.preventDefault();
            isMouseDown = true;
            showPassword();
        });
        
        toggleBtn.addEventListener('mouseup', () => {
            isMouseDown = false;
            hidePassword();
        });
        
        // Eventos para touch (mobile)
        toggleBtn.addEventListener('touchstart', (e) => {
            e.preventDefault();
            showPassword();
        });
        
        toggleBtn.addEventListener('touchend', () => {
            hidePassword();
        });
    }

    get value() {
        return this.root.querySelector('input')?.value || '';
    }

    set value(val) {
        const input = this.root.querySelector('input');
        if (input) {
            input.value = val;
            // Dispatch input event (non-bubbling) to trigger internal listeners (ValidatorMixin and syncValidity)
            input.dispatchEvent(new Event('input', { bubbles: false }));
        }
        this.setAttribute('value', val);
    }
}

customElements.define('flow-input', FlowInput);
